import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Label } from '@/shared/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { TouchTooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import { formatCalendarDate, toCalendarDateString } from '@/shared/lib/tenantDateTime'
import { dataFieldHistoryInstrumentTaskPath } from '@/shared/lib/routes'
import apiClient from '@/shared/lib/apiClient'
import type WebInstrumentsDataFieldHistory from '@/shared/types/web/WebInstrumentsDataFieldHistory'

const CHARTABLE_FIELD_TYPES = new Set(['number', 'defined'])

/** Shared by the Tasks-tab accordion and the Reports-tab Change Log — both shapes only need dataFieldType. */
export function hasChartableDataFieldType(dataFieldType: string | null): boolean {
  return CHARTABLE_FIELD_TYPES.has(dataFieldType ?? '')
}

type DataFieldHistoryEntry = WebInstrumentsDataFieldHistory

interface DataFieldHistoryResponse {
  dataFields: DataFieldHistoryEntry[]
}

type ChartLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; fields: DataFieldHistoryEntry[] }

export interface DataFieldChartDialogProps {
  onClose: () => void
  instrumentId: number
  instrumentTaskId: number
  taskName: string | null
}

// Matches the backend default window when no explicit range is applied.
const DEFAULT_CHART_RANGE_DAYS = 90

export const DataFieldChartDialog = ({
  onClose,
  instrumentId,
  instrumentTaskId,
  taskName,
}: DataFieldChartDialogProps) => {
  // Initialized to loading — the component only mounts when open=true (conditional render in parent).
  const [loadState, setLoadState] = useState<ChartLoadState>({ status: 'loading' })
  // Applied range drives the fetch; the pickers below stage a pending range until "Apply".
  // Empty range = backend default (last 90 days). Legacy exposed From/To pickers.
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string }>({
    from: '',
    to: '',
  })
  // Prefill the pickers with the backend's default window (last 90 days) so the period being
  // shown is explicit instead of a blank "—". appliedRange stays empty so the initial fetch
  // still uses the server default; the visible dates simply mirror it.
  const defaultRange = useMemo(() => {
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - DEFAULT_CHART_RANGE_DAYS)
    return { from: toCalendarDateString(from), to: toCalendarDateString(to) }
  }, [])
  const [fromDate, setFromDate] = useState(defaultRange.from)
  const [toDate, setToDate] = useState(defaultRange.to)

  useEffect(() => {
    let cancelled = false

    const query: Record<string, string> = {}
    if (appliedRange.from) query.from_date = appliedRange.from
    if (appliedRange.to) query.to_date = appliedRange.to
    const queryString = new URLSearchParams(query).toString()
    const basePath = dataFieldHistoryInstrumentTaskPath(instrumentId, instrumentTaskId, {})
    const url = queryString ? `${basePath}?${queryString}` : basePath

    apiClient
      .get<DataFieldHistoryResponse>(url)
      .then((res) => {
        if (!cancelled) setLoadState({ status: 'loaded', fields: res.data.dataFields ?? [] })
      })
      .catch(() => {
        if (!cancelled) setLoadState({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [instrumentId, instrumentTaskId, appliedRange])

  const today = new Date()

  const applyRange = () => {
    setLoadState({ status: 'loading' })
    setAppliedRange({ from: fromDate, to: toDate })
  }

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.show.tasks.chartDialogTitle')}</DialogTitle>
          {taskName && <p className="text-sm text-muted-foreground">{taskName}</p>}
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 border-b pb-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {t('instruments.show.tasks.chartFrom')}
            </Label>
            <DatePickerInput
              value={fromDate}
              onChange={setFromDate}
              maxDate={toDate ? new Date(toDate) : today}
              className="w-40"
              allowTyping
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {t('instruments.show.tasks.chartTo')}
            </Label>
            <DatePickerInput
              value={toDate}
              onChange={setToDate}
              minDate={fromDate ? new Date(fromDate) : undefined}
              maxDate={today}
              className="w-40"
              allowTyping
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={applyRange}
            disabled={loadState.status === 'loading'}
          >
            {t('instruments.show.tasks.chartApply')}
          </Button>
        </div>

        {loadState.status === 'loading' && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              {t('instruments.show.tasks.chartLoading')}
            </span>
          </div>
        )}

        {loadState.status === 'error' && (
          <div className="py-6 text-center">
            <p className="text-sm text-destructive">{t('instruments.show.tasks.chartError')}</p>
          </div>
        )}

        {loadState.status === 'loaded' && loadState.fields.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('instruments.show.tasks.chartNoData')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.fields.length > 0 && (
          <div className="space-y-6">
            {loadState.fields.map((field) => (
              <FieldChart key={field.dataFieldId} field={field} />
            ))}
          </div>
        )}

        <div className="flex justify-end border-t pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('instruments.show.tasks.chartClose')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── FieldChart — per-field chart renderer ──────────────────────────────────

interface FieldChartProps {
  field: DataFieldHistoryEntry
}

const CHART_WIDTH = 520
const CHART_HEIGHT = 120
const CHART_PADDING = { top: 8, right: 8, bottom: 24, left: 36 }
const POINT_INSET = 20

const FieldChart = ({ field }: FieldChartProps) => {
  if (field.dataPoints.length === 0) {
    return (
      <div className="rounded-lg border p-4">
        <p className="text-sm font-medium mb-2">{field.label}</p>
        <p className="text-xs text-muted-foreground">{t('instruments.show.tasks.chartNoData')}</p>
      </div>
    )
  }

  if (field.dataFieldType === 'defined') {
    return <DefinedFieldChart field={field} />
  }

  return <NumberFieldChart field={field} />
}

const NumberFieldChart = ({ field }: FieldChartProps) => {
  // Hover is transient; click pins a selection. The readout below shows the pinned point when
  // one is set, otherwise the hovered one — so clicking a point never clears it just because
  // the cursor is still over it (only a second click on the pinned point deselects).
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const activeIndex = selectedIndex ?? hoverIndex
  const points = field.dataPoints
  const values = points.map((p) => parseFloat(p.value)).filter((v) => !isNaN(v))
  if (values.length === 0) {
    return (
      <div className="rounded-lg border p-4">
        <p className="text-sm font-medium mb-2">{field.label}</p>
        <p className="text-xs text-muted-foreground">{t('instruments.show.tasks.chartNoData')}</p>
      </div>
    )
  }

  const bounds = [field.acceptableLower, field.acceptableUpper].filter(
    (bound): bound is number => bound != null,
  )
  const minVal = Math.min(...values, ...bounds)
  const maxVal = Math.max(...values, ...bounds)
  const range = maxVal - minVal || 1

  const innerW = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right
  const innerH = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom

  // Keep the first/last points off the axes so a point sitting on the min value doesn't
  // visually collide with the Y-axis tick label next to it.
  const plotWidth = Math.max(innerW - POINT_INSET * 2, 1)

  const xFor = (index: number) =>
    CHART_PADDING.left + POINT_INSET + (index / Math.max(points.length - 1, 1)) * plotWidth

  const yFor = (value: number) => CHART_PADDING.top + ((maxVal - value) / range) * innerH

  const pathD = points
    .map((p, i) => {
      const v = parseFloat(p.value)
      if (isNaN(v)) return null
      return `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`
    })
    .filter(Boolean)
    .join(' ')

  const yLabels = [maxVal, (maxVal + minVal) / 2, minVal]

  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm font-medium mb-3">{field.label}</p>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="w-full"
        style={{ maxHeight: 140 }}
        aria-label={field.label ?? undefined}
      >
        {/* Y-axis tick labels */}
        {yLabels.map((v, i) => (
          <text
            key={i}
            x={CHART_PADDING.left - 4}
            y={yFor(v) + 3}
            textAnchor="end"
            fontSize={9}
            fill="#6b7280"
          >
            {v % 1 === 0 ? v : v.toFixed(1)}
          </text>
        ))}

        {/* Acceptable bound reference lines (where defined) */}
        {[field.acceptableLower, field.acceptableUpper]
          .filter((bound): bound is number => bound != null)
          .map((bound, i) => (
            <line
              key={`bound-${i}`}
              x1={CHART_PADDING.left}
              x2={CHART_WIDTH - CHART_PADDING.right}
              y1={yFor(bound)}
              y2={yFor(bound)}
              stroke="#16a34a"
              strokeWidth={1}
              strokeDasharray="4 3"
              opacity={0.6}
            />
          ))}

        {/* Connecting line */}
        {pathD && <path d={pathD} fill="none" stroke="#d1d5db" strokeWidth={1.5} />}

        {/* Data point dots — visual only; hover/selection is handled by the hit-bands below. */}
        {points.map((p, i) => {
          const v = parseFloat(p.value)
          if (isNaN(v)) return null
          const cx = xFor(i)
          const cy = yFor(v)
          const fill = p.acceptableValue ? '#16a34a' : '#dc2626'
          const isActive = activeIndex === i
          return (
            <circle
              key={`dot-${i}`}
              cx={cx}
              cy={cy}
              r={isActive ? 6 : 4}
              fill={fill}
              className="pointer-events-none"
            />
          )
        })}

        {/* Full-height hit-bands: hovering anywhere over a point's X-zone reveals it, so the
            user never has to land exactly on the small dot. Click pins the selection. */}
        {points.map((p, i) => {
          const v = parseFloat(p.value)
          if (isNaN(v)) return null
          const cx = xFor(i)
          const half =
            points.length > 1
              ? innerW / Math.max(points.length - 1, 1) / 2
              : innerW / 2 || CHART_WIDTH / 2
          const bandX = Math.max(cx - half, 0)
          const bandW = Math.min(half * 2, CHART_WIDTH - bandX)
          return (
            <rect
              key={`band-${i}`}
              x={bandX}
              y={0}
              width={bandW}
              height={CHART_HEIGHT}
              fill="transparent"
              className="cursor-pointer"
              onClick={() => setSelectedIndex((current) => (current === i ? null : i))}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex((current) => (current === i ? null : current))}
            />
          )
        })}

        {/* X-axis date labels (first and last) */}
        {points.length > 0 && (
          <>
            <text x={xFor(0)} y={CHART_HEIGHT - 4} textAnchor="middle" fontSize={11} fill="#6b7280">
              {formatCalendarDate(points[0].completedAtIso)}
            </text>
            {points.length > 1 && (
              <text
                x={xFor(points.length - 1)}
                y={CHART_HEIGHT - 4}
                textAnchor="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {formatCalendarDate(points[points.length - 1].completedAtIso)}
              </text>
            )}
          </>
        )}

        {/* Hover/selection tooltip, drawn inside the SVG so it never shifts the surrounding
            layout (an external element toggling on hover was causing flicker + "doubling"). */}
        {activeIndex != null &&
          points[activeIndex] &&
          !isNaN(parseFloat(points[activeIndex].value)) &&
          (() => {
            const point = points[activeIndex]
            const cx = xFor(activeIndex)
            const cy = yFor(parseFloat(point.value))
            const label = `${formatCalendarDate(point.completedAtIso)} · ${point.value}`
            const tooltipWidth = Math.max(label.length * 7 + 20, 80)
            const tooltipHeight = 24
            const rawX = cx - tooltipWidth / 2
            const boxX = Math.min(Math.max(rawX, 2), CHART_WIDTH - tooltipWidth - 2)
            const below = cy - tooltipHeight - 8 < 0
            const boxY = below ? cy + 8 : cy - tooltipHeight - 8
            return (
              <g className="pointer-events-none">
                <rect
                  x={boxX}
                  y={boxY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx={4}
                  fill="#1f2937"
                />
                <circle
                  cx={boxX + 10}
                  cy={boxY + tooltipHeight / 2}
                  r={4}
                  fill={point.acceptableValue ? '#22c55e' : '#ef4444'}
                />
                <text x={boxX + 19} y={boxY + tooltipHeight / 2 + 4} fontSize={12} fill="#ffffff">
                  {label}
                </text>
              </g>
            )
          })()}
      </svg>
      <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-green-600" />
          {t('instruments.show.tasks.chartAcceptable')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-red-600" />
          {t('instruments.show.tasks.chartUnacceptable')}
        </span>
        {bounds.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 border-t border-dashed border-green-600" />
            {t('instruments.show.tasks.chartAcceptableRange')}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Defined Value calendar (REBUILD-336 #1) ──────────────────────────────────
// Renders a month calendar marking each day a value was selected, colored by
// Acceptable (green) / Unacceptable (red). Hover reveals the value(s) for the day.

const DEFINED_CHART_WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const DEFINED_CHART_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

interface DefinedDaySelection {
  values: string[]
  hasUnacceptable: boolean
  // Per-value acceptability, aligned by index with `values`, so the hover tooltip can
  // flag exactly which selected value was out of range on a mixed day.
  entries: { value: string; acceptable: boolean }[]
}

const toLocalDateKey = (isoString: string): string => {
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const DefinedFieldChart = ({ field }: FieldChartProps) => {
  const selectionsByDay = new Map<string, DefinedDaySelection>()
  for (const dataPoint of field.dataPoints) {
    const dayKey = toLocalDateKey(dataPoint.completedAtIso)
    const existing = selectionsByDay.get(dayKey) ?? {
      values: [],
      hasUnacceptable: false,
      entries: [],
    }
    existing.values.push(dataPoint.value)
    existing.entries.push({ value: dataPoint.value, acceptable: dataPoint.acceptableValue })
    if (!dataPoint.acceptableValue) existing.hasUnacceptable = true
    selectionsByDay.set(dayKey, existing)
  }

  const dayKeys = [...selectionsByDay.keys()].sort()
  const firstDay = new Date(`${dayKeys[0]}T00:00:00`)
  const lastDay = new Date(`${dayKeys[dayKeys.length - 1]}T00:00:00`)

  const months: { year: number; month: number }[] = []
  const cursor = new Date(firstDay.getFullYear(), firstDay.getMonth(), 1)
  const end = new Date(lastDay.getFullYear(), lastDay.getMonth(), 1)
  while (cursor <= end) {
    months.push({ year: cursor.getFullYear(), month: cursor.getMonth() })
    cursor.setMonth(cursor.getMonth() + 1)
  }

  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm font-medium mb-3">{field.label}</p>
      <div className="flex flex-wrap gap-6">
        {months.map(({ year, month }) => (
          <DefinedValueMonth
            key={`${year}-${month}`}
            year={year}
            month={month}
            selectionsByDay={selectionsByDay}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-green-600" />
          {t('instruments.show.tasks.chartAcceptable')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-red-600" />
          {t('instruments.show.tasks.chartUnacceptable')}
        </span>
      </div>
    </div>
  )
}

interface DefinedValueMonthProps {
  year: number
  month: number
  selectionsByDay: Map<string, DefinedDaySelection>
}

const DefinedValueMonth = ({ year, month, selectionsByDay }: DefinedValueMonthProps) => {
  const startOffset = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (number | null)[] = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let day = 1; day <= daysInMonth; day++) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks: (number | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))

  const dayKeyFor = (day: number) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        {DEFINED_CHART_MONTHS[month]} {year}
      </p>
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            {DEFINED_CHART_WEEKDAYS.map((weekday) => (
              <th key={weekday} className="w-7 pb-1 text-center font-medium text-muted-foreground">
                {weekday}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => (
            <tr key={weekIndex}>
              {week.map((day, dayIndex) => {
                if (day === null) return <td key={dayIndex} className="p-0.5" />

                const dayKey = dayKeyFor(day)
                const selection = selectionsByDay.get(dayKey)
                const cellColor = selection
                  ? selection.hasUnacceptable
                    ? 'bg-red-100 text-red-800'
                    : 'bg-green-100 text-green-800'
                  : 'text-muted-foreground/60'

                const dayCell = (
                  <span
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded',
                      cellColor,
                      selection && 'cursor-default font-medium',
                    )}
                  >
                    {day}
                  </span>
                )

                if (!selection) {
                  return (
                    <td key={dayIndex} className="p-0.5 text-center">
                      {dayCell}
                    </td>
                  )
                }

                return (
                  <td key={dayIndex} className="p-0.5 text-center">
                    <TouchTooltip>
                      <TooltipTrigger asChild>{dayCell}</TooltipTrigger>
                      <TooltipContent>
                        <p className="mb-1 font-medium">{formatCalendarDate(dayKey)}</p>
                        <ul className="space-y-0.5">
                          {selection.entries.map((entry, entryIndex) => (
                            <li key={entryIndex} className="flex items-center gap-1.5">
                              <span
                                className={cn(
                                  'inline-block h-2 w-2 rounded-full',
                                  entry.acceptable ? 'bg-green-500' : 'bg-red-500',
                                )}
                              />
                              <span>{entry.value}</span>
                              <span className="opacity-70">
                                {entry.acceptable
                                  ? t('instruments.show.tasks.chartAcceptable')
                                  : t('instruments.show.tasks.chartUnacceptable')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </TouchTooltip>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
